import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../axiosBaseQuery';
import LubricationChartsApiParser from './lubricationChartsApiParser';

// ---------- API Slice (merged) ----------
const LubricationChartsApi = createApi({
  reducerPath: 'LubricationChartsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['LubricationChart', 'LubricationChartsData'],
  endpoints: (builder) => ({
    getLubricationChartsListData: builder.query<void, void>({
      query: () => ({
        url: '/vessels/summaries',
        method: 'GET',
      }),
      transformResponse: (res: any) => LubricationChartsApiParser.LubricationChartsListParser(res),
      providesTags: ['LubricationChartsData'],
    }),

    getLubricationProductsByVessel: builder.query<void, number>({
      query: (vesselId: number) => ({
        url: `/vessels/${vesselId}/products`,
        method: 'GET',
      }),
      transformResponse: (res: any) => LubricationChartsApiParser.productsParser(res),
      providesTags: ['LubricationChartsData'],
    }),

    addProductToVessel: builder.mutation<void, any>({
      query: (payload: any) => ({
        url: `/vessels/addVesselProduct`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['LubricationChartsData'],
    }),

    updateVesselProductStatus: builder.mutation<void, any>({
      query: (payload: any) => ({
        url: 'vessels/product/status',
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: ['LubricationChartsData'],
    }),

    deleteVesselProduct: builder.mutation<void, { vesselId: number; productId: number }>({
      query: ({ vesselId, productId }) => ({
        url: `/vessels/product?vesselId=${vesselId}&productId=${productId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LubricationChartsData'],
    }),

    getAllProductsNotInVessel: builder.query<any, number>({
      query: (vesselId: number) => ({
        url: `/lubricant/getAllProductsNotInVesselId?active=true&vesselId=${vesselId}`,
        method: 'GET',
      }),
      providesTags: ['LubricationChartsData'],
    }),

    getActiveProductsBySupplier: builder.query<any, { supplierId: number; vesselId?: number }>({
      query: ({ supplierId, vesselId }) => {
        const params = vesselId ? `?vesselId=${vesselId}` : '';
        return {
          url: `/lubricant/active-by-supplier/${supplierId}${params}`,
          method: 'GET',
        };
      },
      providesTags: ['LubricationChartsData'],
    }),
  }),
});

// ---------- Reducer, Middleware, Path ----------
export const LubricationChartsReducer = LubricationChartsApi.reducer;
export const LubricationChartsMiddleware = LubricationChartsApi.middleware;
export const LubricationChartsReducerPath = LubricationChartsApi.reducerPath;

// ---------- Hooks / Actions ----------
export const {
  useGetLubricationChartsListDataQuery,
  useGetLubricationProductsByVesselQuery,
  useAddProductToVesselMutation,
  useUpdateVesselProductStatusMutation,
  useDeleteVesselProductMutation,
  useGetAllProductsNotInVesselQuery,
  useGetActiveProductsBySupplierQuery,
} = LubricationChartsApi;
